import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { first, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { User } from '@app/_models';
import { AuthenticationService, AlertService } from '@app/_services';
import { ActivatedRoute, Router } from '@angular/router';
import { LazyLoadEvent, MessageService, ConfirmationService } from 'primeng/api';
import { ReservationService } from '@app/_components/reservation';
import { Table } from 'primeng/table';
import { Location } from '@angular/common';
import {
	MatDialog,
	MatDialogConfig,
	MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { ConstantService } from '@app/_services/constant.service';
import * as moment from 'moment';
import { ReservationContainerComponent } from '../reservation/reservation-container/reservation-container.component';
import { OrderComponent } from '@app/_components/order/order/order.component';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { PropertyService } from '@app/_components/property/property.service';
import { ReservationCreateComponent } from '../reservation-create/reservation-create.component';
import { ReservationAdvanceSearchComponent } from '../reservation-advance-search/reservation-advance-search.component';
import { ImportExportComponent } from '@app/_components/_helpers/import-export/import-export.component';

@Component({
	standalone: false,
	selector: 'app-reservation-list',
	templateUrl: './reservation-list.component.html',
	styleUrls: ['./reservation-list.component.css'],
	providers: [MessageService, ConfirmationService, { provide: MAT_DIALOG_DATA, useValue: {} }],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReservationListComponent implements OnInit, OnDestroy {

	private destroy$ = new Subject<void>();

	@ViewChild(ImportExportComponent) importExportComponent: ImportExportComponent;

	datasource: any[];
	reservation: User;
	reservations: any[] = [];
	recordTotal: number;
	lazyLoadEvent: LazyLoadEvent;
	loading = true;
	multiSortMeta = [];
	advanceSearchData: any = [];
	advanceSearchDataFilter: LazyLoadEvent = {};
	eventFilter: LazyLoadEvent = {};

	//For reservation list to be shown on dialog
	formTitle: String;
	reservationForPredicate: any;
	showCreateReservationPopUp = false;

	// forms
	form1: UntypedFormGroup;
	formLoading = false;
	formAlert: String;

	statuses: any;

	validationMessages = {
		shortTermRentalId: [{ type: 'required', message: 'Required' }],
		emailAddress: [{ type: 'required', message: 'Required' },
		{ type: 'email', message: 'Not a valid Email' }],
		referralSourceId: [{ type: 'required', message: 'Required' }],
		numAdults: [{ type: 'required', message: 'Required' }],
		numChildren: [{ type: 'required', message: 'Required' }],
		checkOutDate: [{ type: 'required', message: 'Required' }],
		checkInDate: [{ type: 'required', message: 'Required' }],
		firstName: [{ type: 'required', message: 'Required' }],
		lastName: [{ type: 'required', message: 'Required' }],
	};
	referralSources: any[];
	properties: any[] = [];
	numAdults: any[];
	searchName: any;
	searchData: { [k: string]: any; };
	activatedRoutePropertyCode: any;
	listingTypes: { value: number; name: string; }[];
	reservationTypes: any[];

	// Pre-computed lookup maps — O(1) per lookup instead of O(n) .find() on every row/CD cycle
	statusNameMap = new Map<string, string>();
	statusColorMap = new Map<string, string>();
	resTypeMap = new Map<string, string>();
	listingTypeMap = new Map<any, string>();
	locationMap = new Map<number, string>();


	constructor(
		public authenticationService: AuthenticationService,
		public reservationService: ReservationService,
		public alertService: AlertService,
		public constantService: ConstantService,
		private messageService: MessageService,
		private formBuilder: UntypedFormBuilder,
		public router: Router,
		private _activatedRoute: ActivatedRoute,
		private propertyService: PropertyService,
		private location1: Location,
		private dialog: MatDialog,
		private cdr: ChangeDetectorRef,
		@Inject(MAT_DIALOG_DATA) data: any
	) {
		this.formTitle = data.title,
			this.reservationForPredicate = data.reservation;
		this.referralSources = this.reservationService.referralSources;
		this.numAdults = this.reservationService.numAdults;
		this.listingTypes = this.reservationService.listingTypes;
		this.reservationTypes = this.reservationService.reservationTypes;
		if (this._activatedRoute.snapshot.paramMap.get('propertyCode')) {
			this.activatedRoutePropertyCode = this._activatedRoute.snapshot.paramMap.get('propertyCode');
		}
	}


	ngOnInit() {
		this._activatedRoute.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
			this.searchData = params['searchName'];
			if (this.searchData != null && this.searchData != undefined) {
				this.searchName = this.searchData;
			}
			// Operations-dashboard handoff: a tile click on /operations-dashboard
			// (currently the Occupancy tile) can open this page pre-filtered to a
			// specific reservation set via plain query params. Supported keys:
			//   status         — comma-separated reservation statuses (e.g. "OP,NA,OO,OG")
			//   stayDateStart  — YYYY-MM-DD; lower bound of the stay-overlap window
			//   stayDateEnd    — YYYY-MM-DD; upper bound of the stay-overlap window
			//   locationId     — single locationId to scope to
			// These mirror the field names the advance-search dialog already
			// produces, so the rest of the load path (loadLazy → backend) needs
			// no changes.
			this.applyDashboardQueryFilters(params);
		});
		this.formBuild();
		this.fetchProperties();
		this.buildLookupMaps();
	}

	/**
	 * Human-readable banner shown above the list when the page was opened from
	 * a dashboard tile with filters in the URL. Null when no dashboard handoff
	 * is active. Plain string so it can be bound directly with *ngIf="..."
	 * in the template — no extra object juggling.
	 */
	inheritedFilterSummary: string | null = null;

	/**
	 * Pre-populate {@link advanceSearchData} and {@link advanceSearchDataFilter}
	 * from URL query params so a tile click on the operations dashboard lands
	 * the user on a list already filtered to that scope. The first PrimeNG
	 * lazy-load merges these filters into its query (see loadLazy line ~193).
	 *
	 * Silently ignores unrecognised / malformed params; if no recognised
	 * params are present the list behaves exactly as before.
	 */
	private applyDashboardQueryFilters(params: any): void {
		if (!params) return;
		const statusParam        = params['status'];
		const stayStartParam     = params['stayDateStart'];
		const stayEndParam       = params['stayDateEnd'];
		const checkInStartParam  = params['checkInDateStart'];
		const checkInEndParam    = params['checkInDateEnd'];
		const checkOutStartParam = params['checkOutDateStart'];
		const checkOutEndParam   = params['checkOutDateEnd'];
		const saleStartParam     = params['saleDateStart'];
		const saleEndParam       = params['saleDateEnd'];
		const locationIdParam    = params['locationId'];
		const listingTypeParam   = params['listingType'];
		const listingStatusParam = params['listingStatus'];
		if (!statusParam && !stayStartParam && !stayEndParam && !locationIdParam
			&& !listingTypeParam && !listingStatusParam
			&& !checkInStartParam && !checkInEndParam
			&& !checkOutStartParam && !checkOutEndParam
			&& !saleStartParam && !saleEndParam) return;

		if (!this.advanceSearchDataFilter) this.advanceSearchDataFilter = {};
		if (!this.advanceSearchDataFilter.filters) this.advanceSearchDataFilter.filters = {};
		if (!this.advanceSearchData) this.advanceSearchData = {};

		const summaryParts: string[] = [];

		if (statusParam) {
			const statuses = String(statusParam).split(',').map(s => s.trim()).filter(Boolean);
			if (statuses.length) {
				this.advanceSearchData.reservationStatus = statuses;
				this.advanceSearchDataFilter.filters['reservationStatus'] = { value: statuses, matchMode: 'in' };
				summaryParts.push(`Status: ${statuses.join(' / ')}`);
			}
		}
		if (stayStartParam) {
			this.advanceSearchData.occupyingStartDate = stayStartParam;
			this.advanceSearchDataFilter.filters['stayDateStart'] = { value: moment(stayStartParam).format('MM/DD/YYYY'), matchMode: 'in' };
		}
		if (stayEndParam) {
			this.advanceSearchData.occupyingEndDate = stayEndParam;
			this.advanceSearchDataFilter.filters['stayDateEnd'] = { value: moment(stayEndParam).format('MM/DD/YYYY'), matchMode: 'in' };
		}
		if (stayStartParam || stayEndParam) {
			const s = stayStartParam ? moment(stayStartParam).format('MM/DD/YYYY') : '…';
			const e = stayEndParam   ? moment(stayEndParam).format('MM/DD/YYYY')   : '…';
			summaryParts.push(stayStartParam === stayEndParam
				? `Stays overlapping ${s}`
				: `Stays overlapping ${s} → ${e}`);
		}
		// Check-in date window — used by the Arrivals tile, the per-location
		// Arrivals cell, the Future Occupancy heat-strip cell, and the
		// arrivals-side of the Bookings Created tile when a specific arrival
		// date is highlighted.
		if (checkInStartParam) {
			this.advanceSearchData.arrivalStartDate = checkInStartParam;
			this.advanceSearchDataFilter.filters['checkInDateStart'] = { value: moment(checkInStartParam).format('MM/DD/YYYY'), matchMode: 'in' };
		}
		if (checkInEndParam) {
			this.advanceSearchData.arrivalEndDate = checkInEndParam;
			this.advanceSearchDataFilter.filters['checkInDateEnd'] = { value: moment(checkInEndParam).format('MM/DD/YYYY'), matchMode: 'in' };
		}
		if (checkInStartParam || checkInEndParam) {
			const s = checkInStartParam ? moment(checkInStartParam).format('MM/DD/YYYY') : '…';
			const e = checkInEndParam   ? moment(checkInEndParam).format('MM/DD/YYYY')   : '…';
			summaryParts.push(checkInStartParam === checkInEndParam
				? `Arrivals on ${s}`
				: `Arrivals ${s} → ${e}`);
		}
		// Check-out date window — used by the Departures tile, the per-location
		// Departures cell, and the Future Occupancy strip's "departing" view.
		if (checkOutStartParam) {
			this.advanceSearchData.checkoutStartDate = checkOutStartParam;
			this.advanceSearchDataFilter.filters['checkOutDateStart'] = { value: moment(checkOutStartParam).format('MM/DD/YYYY'), matchMode: 'in' };
		}
		if (checkOutEndParam) {
			this.advanceSearchData.checkoutEndDate = checkOutEndParam;
			this.advanceSearchDataFilter.filters['checkOutDateEnd'] = { value: moment(checkOutEndParam).format('MM/DD/YYYY'), matchMode: 'in' };
		}
		if (checkOutStartParam || checkOutEndParam) {
			const s = checkOutStartParam ? moment(checkOutStartParam).format('MM/DD/YYYY') : '…';
			const e = checkOutEndParam   ? moment(checkOutEndParam).format('MM/DD/YYYY')   : '…';
			summaryParts.push(checkOutStartParam === checkOutEndParam
				? `Departures on ${s}`
				: `Departures ${s} → ${e}`);
		}
		// Sale (booking-creation) date window — used by the Bookings Created
		// pickup tiles so a click on "Today" / "Yesterday" / "Last 7d" /
		// "In Range" lands on reservations whose order was placed in that
		// window. Maps to OrderVO.saleDate on the backend.
		if (saleStartParam) {
			this.advanceSearchData.saleStartDate = saleStartParam;
			this.advanceSearchDataFilter.filters['saleDateStart'] = { value: moment(saleStartParam).format('MM/DD/YYYY'), matchMode: 'in' };
		}
		if (saleEndParam) {
			this.advanceSearchData.saleEndDate = saleEndParam;
			this.advanceSearchDataFilter.filters['saleDateEnd'] = { value: moment(saleEndParam).format('MM/DD/YYYY'), matchMode: 'in' };
		}
		if (saleStartParam || saleEndParam) {
			const s = saleStartParam ? moment(saleStartParam).format('MM/DD/YYYY') : '…';
			const e = saleEndParam   ? moment(saleEndParam).format('MM/DD/YYYY')   : '…';
			summaryParts.push(saleStartParam === saleEndParam
				? `Booked on ${s}`
				: `Booked ${s} → ${e}`);
		}
		if (locationIdParam) {
			const loc = Number(locationIdParam);
			if (!Number.isNaN(loc)) {
				this.advanceSearchData.locationIds = [loc];
				this.advanceSearchDataFilter.filters['shortTermRentalVO.locationId'] = { value: [loc], matchMode: 'in' };
				summaryParts.push(`Location id ${loc}`);
			}
		}
		// listingType + listingStatus — mirror the dashboard's overlap-query
		// scope (active child units only) so the list count tracks the
		// Rooms Occupied tile instead of inflating with composites + inactive.
		// Accept comma-separated values so future callers can pass multiple.
		if (listingTypeParam) {
			const types = String(listingTypeParam).split(',').map(s => s.trim()).filter(Boolean).map(Number).filter(n => !Number.isNaN(n));
			if (types.length) {
				this.advanceSearchDataFilter.filters['shortTermRentalVO.listingType'] = { value: types, matchMode: 'in' };
			}
		}
		if (listingStatusParam) {
			const stats = String(listingStatusParam).split(',').map(s => s.trim()).filter(Boolean).map(Number).filter(n => !Number.isNaN(n));
			if (stats.length) {
				this.advanceSearchDataFilter.filters['shortTermRentalVO.listingStatus'] = { value: stats, matchMode: 'in' };
			}
		}
		if (listingTypeParam || listingStatusParam) {
			// Single rolled-up "scope" label so the banner doesn't get noisy.
			// Mirrors what the dashboard's loadReservationOverlaps enforces.
			summaryParts.push('Active child units only');
		}

		this.inheritedFilterSummary = summaryParts.length ? summaryParts.join('  ·  ') : null;
	}

	/**
	 * Clear the dashboard-inherited filters and reload the list. We do a full
	 * page navigation to /reservations (no query params) rather than just
	 * mutating component state — that's the only path that reliably resets
	 * PrimeNG's internal filter state AND the inherited advanceSearchDataFilter
	 * additions in one shot. Same pattern as the existing reloadPage() helper.
	 */
	clearDashboardFilters(): void {
		// Hide the banner immediately for snappy feedback; the full reload
		// follows. (If the reload is blocked for any reason the banner stays
		// hidden until the next navigation.)
		this.inheritedFilterSummary = null;
		window.location.href = '/reservations';
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private buildLookupMaps() {
		this.constantService.reservationStatuses.forEach(s => {
			this.statusNameMap.set(s.value, s.label);
			this.statusColorMap.set(s.value, s.color);
		});
		this.reservationTypes.forEach(t => this.resTypeMap.set(t.value, t.name));
		this.listingTypes.forEach(t => this.listingTypeMap.set(t.value, t.name));
		this.constantService.locations.forEach(l => this.locationMap.set(l.locationId, l.name));
	}

	fetchProperties() {
		try {
			this.propertyService.findAllSummaryWithStatusActiveAndSuspended()
				.subscribe(
					res => {
						// console.log('findAllPropertiesSummary: ' + JSON.stringify(res));
						this.properties = res.data.properties;
					},
					err => {
						console.log('Feature-Update FAIL: ' + JSON.stringify(err));
						this.alertService.error(err);
					},
					() => { });
		} catch (error) {
			console.log('Form Invalid: ' + JSON.stringify(error));
			this.alertService.error(error);
		}
	}

	formBuild() {
		this.form1 = this.formBuilder.group({
			shortTermRentalId: ['', Validators.required],
			referralSourceId: ['', Validators.required],
			numAdults: ['', Validators.required],
			numChildren: ['', Validators.required],
			checkOutDate: ['', Validators.required],
			checkInDate: ['', Validators.required],
			guestProfile: this.formBuilder.group({
				firstName: ['', Validators.required],
				lastName: ['', Validators.required],
				emailAddress: ['', Validators.compose([Validators.required, Validators.email])],
				guestCompanions: new UntypedFormArray([]),
			}),
		});
	}




	loadLazy(event: LazyLoadEvent) {
		try {
			// Work on a shallow copy to avoid mutating PrimeNG's internal event object
			event = { ...event, filters: { ...(event.filters || {}) }, multiSortMeta: event.multiSortMeta ? [...event.multiSortMeta] : [] };

			if (Object.keys(event.filters).length > 0) {
				this.eventFilter = event.filters;
			}
			if (this.advanceSearchDataFilter.filters && Object.keys(this.advanceSearchDataFilter.filters).length > 0) {
				// Merge advanced-search / dashboard-inherited filters with the column-
				// level filter event. Column filter EMPTY values (e.g. the Status
				// multi-select firing onChange([]) on init) must NOT override an
				// inherited selection — otherwise an inherited Status / Stay-date
				// filter is wiped on the second lazy load. Treat null / undefined /
				// empty-string / empty-array values as "no column filter set".
				const merged: any = { ...this.advanceSearchDataFilter.filters };
				for (const k of Object.keys(event.filters)) {
					const v: any = (event.filters as any)[k];
					const val = v?.value;
					const isEmpty = val == null
						|| val === ''
						|| (Array.isArray(val) && val.length === 0);
					if (!isEmpty) merged[k] = v;
				}
				event.filters = merged;
			}
			this.loading = true;
			this.lazyLoadEvent = event;

			if (Object.keys(event.filters).length > 0) {
				// sort by checkInDateTime when filtering
				event.multiSortMeta.push({ field: 'checkInDateTime', order: -1 });
			} else {
				// sort by createDate by default
				event.multiSortMeta.push({ field: 'createDate', order: -1 });
			}




			//Show only predicate reservation list if it's not null. Used for future occupancy
			if (this.reservationForPredicate != null && this.reservationForPredicate.shortTermRentalId != null) {
				let reservationFilter = { 'shortTermRentalVO.shortTermRentalId': { 'value': this.reservationForPredicate.shortTermRentalId, 'matchMode': 'in' } };
				Object.assign(event.filters, reservationFilter);
				event.multiSortMeta = [];
				event.multiSortMeta.push({ field: 'checkInDateTime', order: -1 });
			}

			if (event.filters?.checkInDateTime) {
				event.filters.checkInDateTime.value = moment(event.filters.checkInDateTime.value).format('MM/DD/YYYY');
			}

			if (event.filters?.checkOutDateTime) {
				event.filters.checkOutDateTime.value = moment(event.filters.checkOutDateTime.value).format('MM/DD/YYYY');
			}

			if (event.filters?.createDate) {
				event.filters.createDate.value = moment(event.filters.createDate.value).format('MM/DD/YYYY');
			}

			if (event.filters?.saleDate) {
				event.filters.saleDate.value = moment(event.filters.saleDate.value).format('MM/DD/YYYY');
			}


			if (this.activatedRoutePropertyCode) {
				// show only activities that are assigned to this activityFeeId
				let rateBucketIdFilter = { 'shortTermRentalVO.propertyCode': { 'value': this.activatedRoutePropertyCode, 'matchMode': 'in' } };
				Object.assign(event.filters, rateBucketIdFilter);
			}
			if (event.filters?.global) {
				event.filters.global.value = event.filters.global.value.trim();
				this.searchName = event.filters.global.value.trim();
			}
			if (event.globalFilter == null && this.searchName != null && this.searchName != "") {
				event.filters.global = {
					matchMode: "contains",
					value: this.searchName
				}
				event.globalFilter = this.searchName;
			}
			const pageNumber = Math.round(event.first / event.rows);
			this.reservationService.findByAgentListFilter(
				pageNumber, event.rows,
				event.sortField,
				event.sortOrder,
				event.multiSortMeta,
				event.globalFilter,
				event.filters).pipe(first()).subscribe(

					res => {
							this.datasource = res.data.reservations;
						this.recordTotal = res.page.recordTotal;
						this.reservations = res.data.reservations;
						this.loading = false;
						this.location1.go('/reservations');
						this.cdr.markForCheck();
					},
					error => {
						this.alertService.error(error);
						this.loading = false;
						this.cdr.markForCheck();
					}
				);
		} catch (error) {
			this.loading = false;
			this.cdr.markForCheck();
		}
	}
	onRowSelect(event) {
		try {
			// use this for adding query params
			// this.router.navigate(['reservation-edit'], { queryParams: { reservationId: event.data.reservationId } });
			// this.router.navigate(['reservations', event.data.reservationId]);
			this.onOpenFormEdit(event.data);
		} catch (error) {
			console.log('onRowSelect(' + event + ')' + error);
		}
	}

	getResStatusName(resStatus: string): string {
		return this.statusNameMap.get(resStatus) ?? '';
	}

	getResStatusColor(resStatus: string): string {
		return this.statusColorMap.get(resStatus) ?? '';
	}
	clear(table: Table) {
		table.clear();
	}

	reloadPage() {
		window.location.reload();
	}

	/*********
		   * Add New Button
		   *
		   */
	onOpenFormAdd() {

		const dialogConfig = new MatDialogConfig();

		// settings
		dialogConfig.disableClose = false;
		dialogConfig.autoFocus = true;
		dialogConfig.hasBackdrop = true;

		dialogConfig.maxWidth = "100vw";
		dialogConfig.width = "95vw";
		dialogConfig.maxHeight = "50vw";
		dialogConfig.height = "95vh";

		// pass data, ie rateBuicket: this.shortTermRentalId,
		dialogConfig.data = {
			title: 'Add Reservation'
		};

		// open the popup and get a reference to it called dialogRef
		const dialogRef = this.dialog.open(ReservationContainerComponent, dialogConfig);

		// to receive the added element, subscribe to the eventEmitter we created 'doAdd' in the element to handle adding
		// dialogRef.componentInstance.doAdd.subscribe(
		// 	data => {
		// 		console.log('Dialog output:', data);

		// 		// when we doAdd in the element, call this parent and update the list so it shows the added element to the database
		// 		this.addReservationCB(data);

		// 		// close the dialogBox
		// 		dialogRef.close();
		// 	});
	}


	/*********
		* Edit Button
		*
		*/
	// onOpenFormEdit(reservation: any) {

	// 	const dialogConfig = new MatDialogConfig();

	// 	// settings
	// 	dialogConfig.disableClose = false;
	// 	dialogConfig.autoFocus = true;
	// 	dialogConfig.hasBackdrop = true;
	//   dialogConfig.maxWidth = "100vw";
	// 		dialogConfig.width = "95vw";
	//     dialogConfig.maxHeight = "50vw";
	// 		dialogConfig.height = "95vh";

	// 	// pass data, ie rateBuicket: this.shortTermRentalId,
	// 	dialogConfig.data = {
	// 		reservationId: reservation.reservationId,
	//     reservation: reservation,
	//     showCloseButton: true,
	// 		title: 'Edit Order'
	// 	};

	// 	// open the popup and get a reference to it called dialogRef
	// 	const dialogRef = this.dialog.open(ReservationContainerComponent, dialogConfig);
	//   this.location1.go('/reservations-edit/' + reservation.reservationId);

	// 	// to receive the added element, subscribe to the eventEmitter we created 'doUpdate' in the element to handle updating the list
	// 	// dialogRef.componentInstance.doUpdate.subscribe(
	// 	// 	data => {
	// 	// 		console.log('Dialog output:', data);

	// 	// 		// when we doAdd in the element, call this parent and update the list so it shows the added element to the database
	// 	// 		this.updateReservationCB(data);

	// 	// 		// close the dialogBox
	// 	// 		dialogRef.close();
	// 	// 	});

	// 	// dialogRef.componentInstance.doDelete.subscribe(
	// 	// 	data => {
	// 	// 		console.log('Dialog output:', data);

	// 	// 		// when we doAdd in the element, call this parent and update the list so it shows the added element to the database
	// 	// 		this.deleteReservationCB(data);

	// 	// 		// close the dialogBox
	// 	// 		dialogRef.close();
	// 	// 	});
	// }

	/*********
		  * Edit Button
		  *
		  */
	onOpenFormEdit(reservation: any) {

		const dialogConfig = new MatDialogConfig();

		// settings
		dialogConfig.disableClose = false;
		dialogConfig.autoFocus = true;
		dialogConfig.hasBackdrop = true;
		dialogConfig.maxWidth = "95vw";
		dialogConfig.width = "95vw";
		dialogConfig.maxHeight = "87vh";
		dialogConfig.height = "87vh";
		dialogConfig.panelClass = "edit-order-mobile";

		// pass data, ie rateBuicket: this.shortTermRentalId,
		dialogConfig.data = {
			reservationId: reservation.reservationId,
			reservation: reservation,
			showCloseButton: true,
			isPopup: true,
			title: 'Edit Order'
		};

		// open the popup and get a reference to it called dialogRef
		const dialogRef = this.dialog.open(OrderComponent, dialogConfig);
		// this.location1.go('/reservations-edit/' + reservation.reservationId);
		this.location1.go('/order/' + reservation.orderId);

		// Reload list when the order is saved
		dialogRef.componentInstance.doUpdate.pipe(takeUntil(this.destroy$)).subscribe(
			data => {
				this.searchName = data;
				this.loadLazy(this.lazyLoadEvent);
				dialogRef.close();
			});

		// Restore URL when dialog is closed (no extra reload)
		dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.location1.go('/reservations');
		});
	}

	/**
		 *
		 * Dialog from Form Callbacks
		 * To edit the list show add/edit/delete in real time from the popup form
		 *
		 */
	addReservationCB(reservation: any) {
		console.log('Adding Reservation: ' + JSON.stringify(reservation));
		// if we don't have a tests array yet...
		if (!this.reservations) {
			this.reservations = [];
		}
		// unshift adds to the front of the array (), push adds to the back
		this.reservations.unshift(reservation);

		// no idea what this is but it is required to update the array
		this.reservations = [...this.reservations];

		this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Reservation ' + reservation.reservationId + ' Added', life: 3000 });
	}

	updateReservationCB(reservation: any) {
		console.log('Updating Reservation: ' + JSON.stringify(reservation));
		let itemIndex = this.reservations.findIndex((item: any) => item.reservationId === reservation.reservationId);
		this.reservations[itemIndex] = reservation;

		// no idea what this is but it is required to update the array
		this.reservations = [...this.reservations];

		this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Reservation ' + reservation.reservationId + ' Updated', life: 3000 });
	}

	deleteReservationCB(reservation: any) {
		console.log('Removing Reservation: ' + reservation);
		this.reservations.splice(this.reservations.indexOf(reservation), 1);
		this.recordTotal = this.reservations.length;

		// no idea what this is but it is required to update the array
		this.reservations = [...this.reservations];

		this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Reservation ' + reservation.reservationId + ' Deleted', life: 3000 });
	}

	onAdvanceSearch() {
		try {

			const dialogConfig = new MatDialogConfig();

			// settings
			dialogConfig.disableClose = true;
			dialogConfig.autoFocus = true;
			dialogConfig.hasBackdrop = true;
			dialogConfig.maxWidth = "45vw";
			dialogConfig.width = "40vw";
			// dialogConfig.height = "45vw";
			// dialogConfig.maxHeight = "60vw";

			// pass data, ie rateBuicket: this.shortTermRentalId,
			dialogConfig.data = {
				advanceSearchData: this.advanceSearchData,
				title: 'Advance Search'
			};

			// open the popup and get a reference to it called dialogRef
			const dialogRef = this.dialog.open(ReservationAdvanceSearchComponent, dialogConfig);
			dialogRef.componentInstance.doSearchCB.subscribe(
				data => {
					this.advanceSearchData = data;
					let event = { "first": 0, "rows": 50, "sortOrder": 1, "globalFilter": null, "multiSortMeta": [] };
					if (this.advanceSearchData) {
						this.advanceSearchDataFilter.filters = {};
						if (this.advanceSearchData.locationIds) {
							let locationIds = [];
							this.advanceSearchData.locationIds.forEach(element => {
								const id = element != null && typeof element === 'object' ? element.locationId : element;
								locationIds.push(id);
							});
							this.advanceSearchDataFilter.filters["shortTermRentalVO.locationId"] = { "value": locationIds, "matchMode": "in" }
						}
						if (this.advanceSearchData.shortTermRentalIds) {
							let shortTermRentalIds = [];
							this.advanceSearchData.shortTermRentalIds.forEach(element => {
								const id = element != null && typeof element === 'object' ? element.shortTermRentalId : element;
								shortTermRentalIds.push(id);
							});
							this.advanceSearchDataFilter.filters["shortTermRentalVO.shortTermRentalId"] = { "value": shortTermRentalIds, "matchMode": "in" }
						}
						if (this.advanceSearchData.arrivalStartDate) {
							this.advanceSearchDataFilter.filters["checkInDateStart"] = { "value": moment(this.advanceSearchData.arrivalStartDate).format('MM/DD/YYYY'), "matchMode": "in" }
						}
						if (this.advanceSearchData.arrivalEndDate) {
							this.advanceSearchDataFilter.filters["checkInDateEnd"] = { "value": moment(this.advanceSearchData.arrivalEndDate).format('MM/DD/YYYY'), "matchMode": "in" }
						}
						if (this.advanceSearchData.saleStartDate) {
							this.advanceSearchDataFilter.filters["saleDateStart"] = { "value": moment(this.advanceSearchData.saleStartDate).format('MM/DD/YYYY'), "matchMode": "in" }
						}
						if (this.advanceSearchData.saleEndDate) {
							this.advanceSearchDataFilter.filters["saleDateEnd"] = { "value": moment(this.advanceSearchData.saleEndDate).format('MM/DD/YYYY'), "matchMode": "in" }
						}
						if (this.advanceSearchData.checkoutStartDate) {
							this.advanceSearchDataFilter.filters["checkOutDateStart"] = { "value": moment(this.advanceSearchData.checkoutStartDate).format('MM/DD/YYYY'), "matchMode": "in" }
						}
						if (this.advanceSearchData.checkoutEndDate) {
							this.advanceSearchDataFilter.filters["checkOutDateEnd"] = { "value": moment(this.advanceSearchData.checkoutEndDate).format('MM/DD/YYYY'), "matchMode": "in" }
						}
						if (this.advanceSearchData.occupyingStartDate) {
							this.advanceSearchDataFilter.filters["stayDateStart"] = { "value": moment(this.advanceSearchData.occupyingStartDate).format('MM/DD/YYYY'), "matchMode": "in" }
						}
						if (this.advanceSearchData.occupyingEndDate) {
							this.advanceSearchDataFilter.filters["stayDateEnd"] = { "value": moment(this.advanceSearchData.occupyingEndDate).format('MM/DD/YYYY'), "matchMode": "in" }
						}
						if (this.advanceSearchData.reservationStatus) {
							this.advanceSearchDataFilter.filters["reservationStatus"] = { "value": this.advanceSearchData.reservationStatus, "matchMode": "in" }
						}
						if (this.advanceSearchData.reservationType) {
							this.advanceSearchDataFilter.filters["reservationType"] = { "value": this.advanceSearchData.reservationType, "matchMode": "in" }
						}
						if (this.advanceSearchData.lastName) {
							this.advanceSearchDataFilter.filters["guestProfileVO.lastName"] = { "value": this.advanceSearchData.lastName, "matchMode": "in" }
						}
						if (this.advanceSearchData.emailAddress) {
							this.advanceSearchDataFilter.filters["guestProfileVO.emailAddress"] = { "value": this.advanceSearchData.emailAddress, "matchMode": "in" }
						}
						if (this.advanceSearchData.wildcardSearch) {
							this.advanceSearchDataFilter.filters["wildcardSearch"] = { "value": this.advanceSearchData.wildcardSearch, "matchMode": "in" }
						}
					}
					if (this.eventFilter && Object.keys(this.eventFilter).length > 0) {
						//combine objects
						this.eventFilter = { ...this.eventFilter, ...this.advanceSearchDataFilter.filters };
						event['filters'] = this.eventFilter;
					}
					this.loadLazy(event);
					// close the dialogBox
					dialogRef.close();
				});
		} catch (error) {
			console.log('onRowSelect(' + this.advanceSearchDataFilter + ')' + error);
		}
	}

	openCreateReservationPopUp() {
		const dialogConfig = new MatDialogConfig();

		// settings
		dialogConfig.disableClose = false;
		dialogConfig.autoFocus = true;
		dialogConfig.hasBackdrop = true;
		dialogConfig.maxWidth = "40vw";
		dialogConfig.width = "20vw";
		dialogConfig.disableClose = true;
		dialogConfig.panelClass = "create-reservation-popup";
		// dialogConfig.maxHeight = "50vw";
		// dialogConfig.height = "95vh";

		// pass data, ie rateBuicket: this.shortTermRentalId,
		dialogConfig.data = {
			showCloseButton: true,
			openNewTab: true
		};

		// open the popup and get a reference to it called dialogRef
		const dialogRef = this.dialog.open(ReservationCreateComponent, dialogConfig);

		// to receive the added element, subscribe to the eventEmitter we created 'doUpdate' in the element to handle updating the list
		dialogRef.componentInstance.doUpdate.subscribe(
			data => {
				console.log('Dialog output:', data);

				// when we doAdd in the element, call this parent and update the list so it shows the added element to the database
				this.loadLazy(this.lazyLoadEvent);

				// close the dialogBox
				dialogRef.close();
			});
	}

	openHelpAlert() {
		this.alertService.success("If you need help or are experiencing any difficulties, please call Dipesh on Ring Central at extension 907.", true);
	}

	exportSelected(exporttype: any) {
		this.importExportComponent.exporData(exporttype, this.reservations, 'Reservations Info');
	}

	getLocationName(locationId: any): string {
		return this.locationMap.get(Number(locationId)) ?? '';
	}

	getListingTypeName(listingType: any): string {
		return this.listingTypeMap.get(listingType) ?? '';
	}

	getReservationTypeName(reservationType: any): string {
		return this.resTypeMap.get(reservationType) ?? '';
	}

}
